import { useCallback, useEffect, useMemo, useState } from 'react';
import { Phone, Tv, Wallet } from 'lucide-react';
import { useDashboardUi } from '@/components/layout/UserLayout';
import { BillIconSelect } from '@/components/bills/BillIconSelect';
import { BillPaymentSummary } from '@/components/bills/BillPaymentSummary';
import { BillPlanPicker } from '@/components/bills/BillPlanPicker';
import { BillToggleGroup } from '@/components/bills/BillToggleGroup';
import { BillValidatedCard } from '@/components/bills/BillValidatedCard';
import { billInputClass, formatBillNaira, formatPlanName } from '@/components/bills/billPaymentUtils';
import { PinVerificationModal } from '@/components/user/PinVerificationModal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { useUserAuth } from '@/context/UserAuthContext';
import { getCableProviderLogo, getCableProviderStyle } from '@/lib/billPricingAssets';
import { billsService, getApiErrorMessage } from '@/services';
import type { BillPlan, BillProduct, SmartcardValidation } from '@/types/bills';
import { cn } from '@/utils/cn';
import { notify } from '@/utils/notify';

function FormLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="mb-2">
      <label className="block text-sm font-medium text-gray-700">{children}</label>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export function CableTvPage() {
  const { user, persistUser } = useUserAuth();
  const { wallet, loadWallet } = useDashboardUi();
  const profilePhone = user?.phone ?? '';

  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);

  const [providers, setProviders] = useState<BillProduct[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [plans, setPlans] = useState<BillPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanCode, setSelectedPlanCode] = useState('');
  const [billersCode, setBillersCode] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [subscriptionType, setSubscriptionType] = useState<'change' | 'renew'>('change');

  const [validating, setValidating] = useState(false);
  const [smartcardValidation, setSmartcardValidation] = useState<SmartcardValidation | null>(null);

  const [showPinModal, setShowPinModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  const walletBalance = wallet?.balance ?? user?.walletBalance ?? 0;
  const selectedProvider = useMemo(
    () => providers.find((p) => p.serviceId === selectedProviderId) ?? null,
    [providers, selectedProviderId],
  );
  const selectedPlan = useMemo(
    () => plans.find((p) => p.variationCode === selectedPlanCode) ?? null,
    [plans, selectedPlanCode],
  );

  const usesPhone = selectedProvider?.usesPhoneAsBiller ?? false;
  const supportsRenewal = selectedProvider?.supportsRenewal ?? false;
  const needsSmartcardVerify = !usesPhone;

  const providerOptions = useMemo(
    () =>
      providers.map((provider) => {
        const style = getCableProviderStyle(provider.serviceId);
        return {
          value: provider.serviceId,
          label: provider.fullName,
          sublabel: provider.name.toUpperCase(),
          logoUrl: getCableProviderLogo(provider.serviceId),
          fallbackColor: style.color,
          fallbackInitial: style.label,
        };
      }),
    [providers],
  );

  const planOptions = useMemo(
    () =>
      plans.map((plan) => ({
        value: plan.variationCode,
        label: formatPlanName(plan.name),
        price: plan.price ?? plan.subscriberPrice,
        leadingIcon: <Tv className="mt-0.5 h-4 w-4 shrink-0 text-secondary-500" />,
      })),
    [plans],
  );

  const amountToPay = useMemo(() => {
    if (subscriptionType === 'renew' && smartcardValidation?.renewalAmount) {
      return smartcardValidation.renewalAmount;
    }
    return selectedPlan?.price ?? selectedPlan?.subscriberPrice ?? null;
  }, [selectedPlan, smartcardValidation, subscriptionType]);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billsService.getCableProviders();
      const data = res.data;
      setProviders(data?.providers ?? []);
      setConfigured(Boolean(data?.configured));
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load TV providers'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPlans = useCallback(async (serviceId: string) => {
    setPlansLoading(true);
    try {
      const res = await billsService.getCablePlans(serviceId);
      const loaded = res.data?.plans ?? [];
      setPlans(loaded);
      setSelectedPlanCode(loaded[0]?.variationCode ?? '');
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load bouquets'));
      setPlans([]);
      setSelectedPlanCode('');
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    if (selectedProviderId) {
      void loadPlans(selectedProviderId);
      setCustomerPhone(profilePhone.replace(/\D/g, '').slice(0, 11));
    } else {
      setPlans([]);
      setSelectedPlanCode('');
    }
    setSmartcardValidation(null);
    setSubscriptionType('change');
    setBillersCode('');
  }, [selectedProviderId, loadPlans, profilePhone]);

  useEffect(() => {
    setSmartcardValidation(null);
  }, [billersCode, customerPhone, selectedProviderId, selectedPlanCode, subscriptionType]);

  async function handleValidate() {
    if (!selectedProvider) return;
    if (subscriptionType === 'change' && !selectedPlan) {
      notify.error('Select a bouquet plan before validating');
      return;
    }
    if (billersCode.length < 10) {
      notify.error('Enter a valid smartcard number (at least 10 digits)');
      return;
    }
    if (customerPhone.length < 11) {
      notify.error('Enter a valid customer phone (11 digits)');
      return;
    }
    setValidating(true);
    try {
      const res = await billsService.validateSmartcard({
        serviceId: selectedProvider.serviceId,
        smartcardNumber: billersCode,
        phone: customerPhone,
        variationCode: subscriptionType === 'change' ? selectedPlan?.variationCode : undefined,
      });
      const data = res.data ?? null;
      setSmartcardValidation(data);
      if (data?.renewalAmount && supportsRenewal) {
        setSubscriptionType('renew');
      }
      notify.success('Smartcard validated');
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Validation failed'));
    } finally {
      setValidating(false);
    }
  }

  function handlePayClick() {
    if (customerPhone.length < 11) {
      notify.error('Enter a valid customer phone');
      return;
    }
    if (!usesPhone && !smartcardValidation?.customerName) {
      notify.error('Validate smartcard before paying');
      return;
    }
    setShowPinModal(true);
  }

  async function handlePurchase(pin: string) {
    if (!selectedProvider || !amountToPay) return;
    const customerName = smartcardValidation?.customerName?.trim();
    if (!customerName) {
      notify.error('Customer name is required — validate smartcard first');
      return;
    }
    setPurchasing(true);
    try {
      const res = await billsService.purchaseCableTv({
        serviceId: selectedProvider.serviceId,
        billersCode: usesPhone ? customerPhone : billersCode,
        variationCode: subscriptionType === 'change' ? selectedPlan?.variationCode : undefined,
        amount: subscriptionType === 'renew' ? amountToPay : undefined,
        phone: customerPhone,
        customerName,
        subscriptionType,
        pin,
      });
      const data = res.data;
      if (data?.user) persistUser({ ...user!, walletBalance: data.user.walletBalance });
      await loadWallet();
      notify.success(data?.message ?? 'Subscription successful');
      setShowPinModal(false);
      setSmartcardValidation(null);
      setBillersCode('');
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Purchase failed'));
    } finally {
      setPurchasing(false);
    }
  }

  const validated = usesPhone
    ? Boolean(selectedPlan) && customerPhone.length >= 11
    : Boolean(smartcardValidation?.customerName);
  const canValidateSmartcard =
    needsSmartcardVerify &&
    selectedProvider &&
    billersCode.length >= 10 &&
    customerPhone.length >= 11 &&
    !smartcardValidation &&
    (subscriptionType === 'renew' || Boolean(selectedPlan));

  const canPay =
    configured &&
    selectedProvider &&
    validated &&
    amountToPay != null &&
    walletBalance >= amountToPay &&
    customerPhone.length >= 11 &&
    (usesPhone ? Boolean(selectedPlan) : billersCode.length >= 10) &&
    (subscriptionType === 'renew' || selectedPlan);

  const summaryRows = [
    { label: 'Provider', value: selectedProvider?.fullName ?? 'Not selected' },
    {
      label: 'Plan',
      value:
        subscriptionType === 'renew'
          ? smartcardValidation?.currentBouquet
            ? `Renew · ${smartcardValidation.currentBouquet}`
            : 'Renew current bouquet'
          : selectedPlan
            ? formatPlanName(selectedPlan.name)
            : '—',
    },
    {
      label: usesPhone ? 'Showmax phone' : 'Smartcard',
      value: usesPhone ? customerPhone || '—' : billersCode || '—',
    },
    { label: 'Customer phone', value: customerPhone || '—' },
    ...(smartcardValidation && !usesPhone
      ? [{ label: 'Customer', value: smartcardValidation.customerName }]
      : []),
  ];

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader label="Loading cable TV..." />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 pb-28 md:pb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Buy Cable TV Subscription</h1>
        <p className="text-sm text-gray-500">
          Choose provider and plan, validate smartcard, then pay from wallet.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="overflow-hidden border-gray-200 p-0 shadow-md">
          <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
            <h2 className="font-semibold text-gray-900">Subscription details</h2>
          </div>
          <div className="space-y-5 p-5">
            <BillIconSelect
              label="Select cable TV provider"
              placeholder="Choose a provider"
              sheetTitle="Cable TV providers"
              value={selectedProviderId}
              options={providerOptions}
              onChange={setSelectedProviderId}
            />

            {subscriptionType !== 'renew' && (
              <div className={cn(!selectedProvider && 'pointer-events-none opacity-50')}>
                <BillPlanPicker
                  label="Select plan"
                  placeholder={selectedProvider ? 'Choose a bouquet' : 'Select a provider first'}
                  sheetTitle="Bouquet plans"
                  value={selectedPlanCode}
                  options={planOptions}
                  onChange={setSelectedPlanCode}
                  disabled={!selectedProvider}
                  loading={plansLoading}
                />
              </div>
            )}

            <div className={cn(!selectedProvider && 'pointer-events-none opacity-50')}>
              <FormLabel hint="Required by Otobill — format 08123456789. Prefills from your profile.">
                Customer phone
              </FormLabel>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  inputMode="numeric"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  placeholder="08123456789"
                  className={cn(billInputClass, 'pl-10')}
                />
              </div>
            </div>

            {usesPhone ? (
              <div className={cn(!selectedProvider && 'pointer-events-none opacity-50')}>
                <FormLabel hint="Showmax uses the customer phone above as the account.">
                  Showmax account
                </FormLabel>
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
                  {customerPhone ? (
                    <span className="font-medium text-gray-900">{customerPhone}</span>
                  ) : (
                    <span className="text-amber-700">Enter customer phone above</span>
                  )}
                </div>
              </div>
            ) : (
              <div className={cn(!selectedProvider && 'pointer-events-none opacity-50')}>
                <FormLabel hint="Validate smartcard after selecting a plan.">
                  Smartcard / IUC number
                </FormLabel>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={billersCode}
                    onChange={(e) => setBillersCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter smartcard number"
                    className={cn(billInputClass, 'flex-1')}
                  />
                  {canValidateSmartcard && (
                    <Button
                      type="button"
                      size="sm"
                      className="shrink-0 px-4"
                      loading={validating}
                      onClick={() => void handleValidate()}
                    >
                      Validate
                    </Button>
                  )}
                </div>
              </div>
            )}

            {smartcardValidation && !usesPhone && (
              <BillValidatedCard title="Smartcard validated">
                <p className="font-medium">{smartcardValidation.customerName}</p>
                {smartcardValidation.status && <p>Status: {smartcardValidation.status}</p>}
                {smartcardValidation.currentBouquet && (
                  <p>Current bouquet: {smartcardValidation.currentBouquet}</p>
                )}
                {smartcardValidation.renewalAmount != null && (
                  <p>Renewal amount: {formatBillNaira(smartcardValidation.renewalAmount)}</p>
                )}
              </BillValidatedCard>
            )}

            {supportsRenewal && smartcardValidation && (
              <div>
                <FormLabel>Subscription action</FormLabel>
                <BillToggleGroup
                  value={subscriptionType}
                  onChange={setSubscriptionType}
                  options={[
                    { value: 'change', label: 'Change bouquet' },
                    { value: 'renew', label: 'Renew current' },
                  ]}
                />
              </div>
            )}

            <Button fullWidth disabled={!canPay} onClick={handlePayClick}>
              <Wallet className="h-4 w-4" />
              Pay {amountToPay ? formatBillNaira(amountToPay) : '—'} · Wallet{' '}
              {formatBillNaira(walletBalance)}
            </Button>
          </div>
        </Card>

        <div className="hidden md:block">
          <Card className="sticky top-24 border-gray-200 p-0 shadow-md">
            <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
              <div className="flex items-center gap-2">
                <Tv className="h-5 w-5 text-brand-600" />
                <div>
                  <h2 className="font-semibold text-gray-900">Payment summary</h2>
                  <p className="text-xs text-gray-500">Review your subscription</p>
                </div>
              </div>
            </div>
            <div className="space-y-4 p-5">
              <BillPaymentSummary
                rows={summaryRows}
                total={amountToPay}
                walletBalance={walletBalance}
                reversalNotice="Please ensure your smartcard number is correct. Cable TV payments are processed instantly and cannot be reversed."
              />
            </div>
          </Card>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-[4.5rem] z-40 border-t border-gray-200 bg-white/95 p-3 backdrop-blur md:hidden">
        <Button fullWidth disabled={!canPay} onClick={handlePayClick}>
          Pay {amountToPay ? formatBillNaira(amountToPay) : '—'} · Wallet{' '}
          {formatBillNaira(walletBalance)}
        </Button>
      </div>

      <PinVerificationModal
        open={showPinModal}
        onClose={() => setShowPinModal(false)}
        onConfirm={(pin) => void handlePurchase(pin)}
        loading={purchasing}
        title="Confirm TV subscription"
      />
    </div>
  );
}
